import { daysAgo } from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { fancyAlert } from "components/ui/fancy-alert";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { Spinner } from "components/ui/spinner";
import * as React from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Actions } from "store/actions";
import { GlobalSuccessNotificationCreate } from "store/actions/global-notifications";
import { TransferSubjectHeading } from "./transfer-subject-heading";
import { ClientTransferRequestSummary } from "./transfer-types";

interface Props {
    clientTransferRequest: ClientTransferRequestSummary;
    onTransferChanged: () => void;
}

export const OutgoingTransferCard: React.FunctionComponent<Props> = (props: Props) => {
    const { clientTransferRequest, onTransferChanged } = props;
    const globalDispatch = useDispatch();
    const trpcContext = trpc.useTRPC();

    const cancelTransfer = useMutation(
        trpcContext.clientTransfer.cancel.mutationOptions(),
    );
    const resendTransfer = useMutation(
        trpcContext.clientTransfer.resend.mutationOptions(),
    );

    const onCancelTransfer = React.useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();

            const confirm = await fancyConfirm(
                "Cancel transfer?",
                "This will cancel the transfer request. The client will not be transferred.",
                "Yes, cancel",
                "No, go back",
            );

            if (!confirm) {
                return;
            }

            try {
                await cancelTransfer.mutateAsync({
                    clientTransferRequestId: clientTransferRequest.id,
                });
            } catch {
                await fancyAlert(
                    "Unable to cancel transfer",
                    "This transfer request may have already been accepted, declined, or cancelled.",
                );
            }
            onTransferChanged();
        },
        [cancelTransfer, onTransferChanged, clientTransferRequest.id],
    );

    const onResendTransfer = React.useCallback(async () => {
        try {
            await resendTransfer.mutateAsync({
                clientTransferRequestId: clientTransferRequest.id,
            });
        } catch {
            await fancyAlert(
                "Unable to resend transfer",
                "This transfer request may have already been accepted, declined, or cancelled.",
            );
            onTransferChanged();
            return;
        }
        const successNotification: GlobalSuccessNotificationCreate = {
            messageId: "resend-client-transfer",
            messageTitle: "Sent!",
            messageBody: "Client transfer notification sent.",
            type: Actions[Actions.GLOBAL_SUCCESS_NOTIFY],
        };
        globalDispatch(successNotification);
    }, [resendTransfer, clientTransferRequest.id, globalDispatch, onTransferChanged]);

    return (
        <div className="col-span-1 divide-y divide-gray-200 rounded md:rounded-lg bg-white shadow">
            <div className="flex-1 overflow-auto p-4">
                <Link
                    to={clientTransferRequest.subjectAppLink}
                    className="twoverride text-gray-900 text-lg">
                    <TransferSubjectHeading
                        clientTransferRequest={clientTransferRequest}
                    />
                </Link>
            </div>
            <div className="flex-1 overflow-auto">
                <ul className="p-4 space-y-2">
                    <li>Sent by: {clientTransferRequest.sentByName}</li>
                    <li>To: {clientTransferRequest.sentToName}</li>
                    <li>
                        Last email sent {daysAgo(clientTransferRequest.lastEmailSentAt)}
                    </li>
                </ul>
            </div>
            <div className="-mt-px flex divide-x divide-gray-200 cursor-pointer">
                <div className="flex w-0 flex-1">
                    <a
                        onClick={onCancelTransfer}
                        className="relative inline-flex w-0 flex-1 items-center justify-center border border-transparent py-4 text-sm font-medium text-red-600 bg-red-100 hover:bg-gray-100 hover:text-gray-800 twoverride rounded-bl-lg">
                        <span className="ml-3">Cancel transfer</span>
                        {cancelTransfer.isPending ? <Spinner /> : null}
                    </a>
                    <a
                        onClick={onResendTransfer}
                        className="relative inline-flex w-0 flex-1 items-center justify-center border border-transparent py-4 text-sm font-medium text-brand-600 bg-brand-100 hover:bg-gray-100 hover:text-gray-800 twoverride rounded-br-lg">
                        <span className="ml-3">Resend</span>
                        {resendTransfer.isPending ? <Spinner /> : null}
                    </a>
                </div>
            </div>
        </div>
    );
};
